import { plusFill } from "@/components/icons/SVGIcons";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { addNewFillToBlock } from "../../ActionSummaryTableHelperFunctions";
import tradeDataStore from "@/store/tradeDataStore";

/**
 * AddFillButtonCellRenderer
 *
 * Renders an "Add" button in the Order Fills table.
 * When clicked, adds a new fill entry to the block's fills array.
 * Only shown for RP trades.
 */
const AddFillButtonCellRenderer = (params: any) => {
  // Subscribe to tradeData for reactive updates (hooks must run before any early return)
  const tradeData = tradeDataStore((state) => state.tradeData);

  if (!params.data) {
    return null;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;
  const isRP = fullTradeData?.role === "RP";

  const currentTrade = tradeData.find((trade) => trade.esid === esid);
  const status = currentTrade?.status ?? fullTradeData?.status;

  // Hide at pre-Working statuses (fills can't be added yet)
  // Hide at Filled/NoMoreFill (trade is complete, RP can't edit)
  // Show at ChangePending (RP is editing again) and Working
  // Remove at affirmed as the RP needs to go back to edit.
  if (
    status == ExecutionStrategyStatus.Received ||
    status == ExecutionStrategyStatus.ReSent ||
    status == ExecutionStrategyStatus.Acknowledged ||
    status == ExecutionStrategyStatus.Rejected ||
    status == ExecutionStrategyStatus.Filled ||
    status == ExecutionStrategyStatus.NoMoreFills ||
    status == ExecutionStrategyStatus.Affirmed ||
    status == ExecutionStrategyStatus.Recalled ||
    status == ExecutionStrategyStatus.NotDealt
  ) {
    return null;
  }

  // Only show button for RP trades
  if (!isRP) {
    return null;
  }

  const handleAddFill = () => {
    if (esid && blockId !== undefined) {
      addNewFillToBlock(esid, blockId);

      // For flattened fills: add the new fill row to the grid via transaction
      if (params.data?.rowType === "block" && params.api) {
        // Get the newly added fill from the store
        const updatedTradeData = tradeDataStore.getState().tradeData;
        const updatedTrade = updatedTradeData.find((t) => t.esid === esid);
        const updatedLeg = updatedTrade?.legs?.find(
          (l) => l.blockId === blockId,
        );
        const newFill = updatedLeg?.fills?.[updatedLeg.fills.length - 1];

        if (newFill && params.data) {
          // Use extFillId for stable row identification (survives backend fillId changes)
          const stableFillId = newFill.extFillId ?? newFill.fillId;
          // Construct flattened fill row matching flattenBlocksAndFills format
          const newFillRow = {
            ...params.data, // Inherit block data
            rowType: "fill",
            parentBlockId: blockId,
            fillData: { ...newFill, blockId },
            fillId: newFill.fillId,
            dataPath: [`block-${blockId}`, `fill-${blockId}-${stableFillId}`],
            fills: undefined,
          };

          // Find the current block's index so we can insert the fill immediately after it
          let insertIndex = -1;
          params.api.forEachNodeAfterFilterAndSort(
            (node: any, index: number) => {
              if (
                node.data?.blockId === blockId &&
                node.data?.rowType === "block"
              ) {
                // Count existing fills for this block to insert at the end of the fill group
                let fillCount = 0;
                params.api.forEachNode((n: any) => {
                  if (
                    n.data?.rowType === "fill" &&
                    n.data?.parentBlockId === blockId
                  ) {
                    fillCount++;
                  }
                });
                insertIndex = index + fillCount + 1;
              }
            },
          );

          params.api.applyTransaction({
            add: [newFillRow],
            addIndex: insertIndex > -1 ? insertIndex : undefined,
          });
        }

        // Ensure the block is expanded (remove from collapsed set) so the fill is visible
        const collapsedBlocks = params.context?.collapsedFillBlocks;
        if (collapsedBlocks?.has(blockId)) {
          collapsedBlocks.delete(blockId);
          params.api.onFilterChanged();
        }

        params.api.refreshCells({ force: true, suppressFlash: true });
      } else {
        if (params.api) {
          params.api.refreshCells({ force: true, suppressFlash: true });
        }
      }
    }
  };

  return (
    <div
      data-testid="addFillButtonCellContainer"
      className="flex h-full items-center justify-center"
    >
      <button
        onClick={handleAddFill}
        className="flex min-h-[20px] min-w-[20px] items-center justify-center rounded bg-gray-400/50 text-xs font-semibold text-gray-600 opacity-90 hover:bg-green-600/70 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:bg-gray-500/50 dark:text-white dark:hover:bg-green-600/70"
        title="Add new fill"
      >
        {plusFill}
      </button>
    </div>
  );
};

export default AddFillButtonCellRenderer;
